import { Button, Loading, Typography } from '@p2p/components';
import { MainContainer, usePaymentData } from '@p2p/entities/payment';
import { PaymentCancelModal } from '@p2p/entities/templates';
import { useConfirmAmountMutation, useGetAvailableAmountsQuery } from '@p2p/services';
import {
  MSP_CONFIRM_AMOUNT_SESSION_KEY,
  MSP_PAGE,
  NOT_FOUND_PAYMENT,
  ORDER_STATUS,
} from '@p2p/shared/constants';
import { formatCurrencyAmount } from '@p2p/shared/functions';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { AmountContainer } from './components/AmountContainer';
import styles from './SelectAmountPage.module.scss';

export const SelectAmountPage = () => {
  const { order, pageSettings } = usePaymentData();
  const navigate = useNavigate();

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const isInitOrder = order.status === ORDER_STATUS.INIT;
  const {
    data: availableAmounts,
    isLoading,
    isError,
    error: errorGetAvailableAmountsResponse,
  } = useGetAvailableAmountsQuery(order.id, { skip: !isInitOrder });

  const [confirmAmount] = useConfirmAmountMutation();

  const handleContinue = () => {
    if (selectedAmount === null) return;

    sessionStorage.setItem(MSP_CONFIRM_AMOUNT_SESSION_KEY, order.id);
    confirmAmount({ order_uuid: order.id, amount: selectedAmount });
    navigate(`${MSP_PAGE}/${order.id}/create-requisite`);
  };

  if (!isInitOrder) {
    return <Navigate to={`${MSP_PAGE}/${order.id}/details`} replace />;
  }

  if (
    isError &&
    errorGetAvailableAmountsResponse &&
    'status' in errorGetAvailableAmountsResponse &&
    errorGetAvailableAmountsResponse.status === 404
  ) {
    return <Navigate to={`/${order.id}/${NOT_FOUND_PAYMENT}`} replace />;
  }

  if (sessionStorage.getItem(MSP_CONFIRM_AMOUNT_SESSION_KEY) === order.id) {
    return <Navigate to={`${MSP_PAGE}/${order.id}/create-requisite`} replace />;
  }

  if (isLoading || !availableAmounts) {
    return <Loading />;
  }

  return (
    <>
      <MainContainer className={styles.container}>
        <div className={styles.heading}>
          <Typography className={styles.headingTitle} variant="h1" color="heading" component="h1">
            Выберите другую сумму для совершения платежа
          </Typography>
          <Typography className={styles.headingSubtitle} variant="text-l-medium" noWrap>
            Оплатить ровно {formatCurrencyAmount(order.expected_income, { showSymbol: true })}{' '}
            сейчас не получится
          </Typography>
        </div>
        <AmountContainer
          recommendedAmounts={availableAmounts.recommended_amounts}
          otherAmounts={availableAmounts.other_amounts}
          selectedAmount={selectedAmount}
          onSelect={setSelectedAmount}
        />
        <div className={styles.actions}>
          <Button disabled={!selectedAmount} onClick={handleContinue}>
            Продолжить
          </Button>
          <Button onClick={() => setIsCancelModalOpen(true)} variant="outlined">
            Отменить заявку
          </Button>
        </div>
      </MainContainer>
      <PaymentCancelModal
        isActive={isCancelModalOpen}
        setIsActive={setIsCancelModalOpen}
        pageSettings={pageSettings}
        orderId={order.id}
        cancelPath={`${MSP_PAGE}/${order.id}/canceled`}
        orderStatus={order.status}
      />
    </>
  );
};
